/**
 * Which brands and manufacturers an organization owns, and how the two sources
 * of that answer are folded into one list.
 *
 * Ownership is recorded twice during the dual-write phase:
 *
 * - the LEGACY free-text `parentOrg` string on each brand/manufacturer document,
 *   matched through the organization's `alternativeNames`, and
 * - the NEW `parentOrgId` foreign key, which nothing writes until Phase 3.
 *
 * So today every row comes from the string and the FK contributes nothing. That
 * is not a reason to leave the FK out: the moment Phase 3's backfill runs, rows
 * start arriving from it with no change here, and a brand that has been
 * backfilled but whose legacy string was cleared would otherwise vanish from the
 * portfolio.
 *
 * Kept free of Algolia, Firestore and React so the matching rule — the part that
 * decides whether `rolex_sa` on a brand means THIS organization — is testable on
 * its own.
 */
import { parentOrgMatchKey } from './parentOrgSeed';

/** One brand or manufacturer in an organization's portfolio. */
export interface PortfolioEntry {
  /** Firestore doc id — `watchBrands/{id}` or `movement_manufacturers/{id}`. */
  id: string;
  name: string;
  /** How this entry was found. The FK wins when a document carries both. */
  via: 'parentOrgId' | 'parentOrg';
  /** The legacy string that matched, for the "matched as …" hint. */
  matchedValue?: string;
}

export interface Portfolio {
  brands: PortfolioEntry[];
  manufacturers: PortfolioEntry[];
}

/** The org fields that can legitimately claim a legacy `parentOrg` string. */
export interface PortfolioOrg {
  id: string;
  name?: string;
  displayName?: string;
  alternativeNames?: string[];
}

/**
 * Every comparison key this organization answers to.
 *
 * The same set `countParentOrgUsage` builds, and deliberately the same function:
 * if the listing's count and the editor's list ever disagreed about what
 * "Swatch Group" matches, one of them would be lying, and there would be no way
 * to tell which from the screen.
 */
export function orgMatchKeys(org: PortfolioOrg): Set<string> {
  return new Set(
    [org.id, org.name, org.displayName, ...(org.alternativeNames ?? [])]
      .map((value) => parentOrgMatchKey(value ?? ''))
      .filter(Boolean),
  );
}

/**
 * The RAW `parentOrg` strings, out of an index facet, that belong to this
 * organization.
 *
 * Returned raw rather than normalised because the caller feeds them straight
 * back to Algolia as `facetFilters`, which match the stored string exactly — a
 * slugged key would match nothing.
 */
export function portfolioFacetValues(
  org: PortfolioOrg,
  facetValues: readonly string[],
): string[] {
  const keys = orgMatchKeys(org);
  return facetValues.filter((value) => keys.has(parentOrgMatchKey(value)));
}

/**
 * Fold the FK rows and the legacy-string rows into one list per entity type.
 *
 * A document found by both is ONE entry credited to the FK: during dual-write
 * most owned documents will carry both, and showing Breguet twice — once as a
 * string match and once as a foreign key — would read as two brands.
 */
export function mergePortfolio(
  fromFk: { brands: PortfolioEntry[]; manufacturers: PortfolioEntry[] },
  fromLegacy: { brands: PortfolioEntry[]; manufacturers: PortfolioEntry[] },
): Portfolio {
  const merge = (fk: PortfolioEntry[], legacy: PortfolioEntry[]): PortfolioEntry[] => {
    const byId = new Map<string, PortfolioEntry>();
    for (const entry of fk) byId.set(entry.id, entry);
    for (const entry of legacy) if (!byId.has(entry.id)) byId.set(entry.id, entry);
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  };
  return {
    brands: merge(fromFk.brands, fromLegacy.brands),
    manufacturers: merge(fromFk.manufacturers, fromLegacy.manufacturers),
  };
}

/** Nothing owned at all — the zero-state asks a different question from "none yet". */
export function isEmptyPortfolio(portfolio: Portfolio): boolean {
  return portfolio.brands.length === 0 && portfolio.manufacturers.length === 0;
}
